import { useParams } from 'react-router-dom';
import React, { useState } from 'react';

import { useFormik } from 'formik';
import { ApplicationDeclaration, PAGE_TEXT } from '..';
import { boolean, object, string } from 'yup';
import {
  Alert,
  ErrorPage,
  Loading,
  type AlertDetails,
} from '../../../../../components';
import { debounce } from '../../../../../utils';
import { updateApplicationItem } from '../../../graphql';
import { type ApplicationDeclarationData } from '../Declaration/types';

export const MakeUpDeclaration: React.FC = () => {
  const { applicationId: id } = useParams();

  if ((id ?? '').length === 0) {
    return <ErrorPage errorCode={404} />;
  }

  const [loading, setLoading] = useState(false);

  const [openAlert, setOpenAlert] = useState(false);

  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const updateApplicationForm = updateApplicationItem();

  const initialValues: ApplicationDeclarationData = {
    personalType: undefined,
    confirmation: false,
    informationAccuracy: false,
    engagement: undefined,
  };

  const declarationFormik = useFormik<ApplicationDeclarationData>({
    initialValues,
    validationSchema: object({
      confirmation: boolean().required().oneOf([true]),
      informationAccuracy: boolean().required().oneOf([true]),
      personalType: string().required(),
      engagement: boolean().required(),
    }),
    onSubmit: async values => {},
  });

  const updateDeclarationForm = debounce(async () => {
    setLoading(true);

    try {
      await updateApplicationForm({
        variables: {
          id,
          updateId: '',
          input: {
            declaration: {
              ...declarationFormik.values,
              applicationId: id,
            },
          },
        },
      });

      setLoading(false);

      setAlertDetails({
        alertContent: PAGE_TEXT.MAKE_UP_DECLARATION.SUCCESSFUL_ALERT,
        alertType: 'success',
      });
      setOpenAlert(true);

      window.location.href = '/applications';
    } catch (e) {
      setLoading(false);
      setAlertDetails({
        alertContent: `Submission Failed.${(e as Error)?.message}`,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  }, 1000);

  const handleSubmission = async (): Promise<void> => {
    declarationFormik.handleSubmit();

    const errors = await declarationFormik.validateForm();
    const errorKeys = Object.keys(errors);

    if (errorKeys.length !== 0) {
      setAlertDetails({
        alertContent: PAGE_TEXT.MAKE_UP_DECLARATION.NOT_COMPLETE_WARNING,
        alertType: 'warning',
      });
      setOpenAlert(true);

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    } else {
      updateDeclarationForm();
    }
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            margin: '2rem',
            alignItems: 'center',
          }}
        >
          <ApplicationDeclaration
            formik={declarationFormik}
            enableEdit={true}
            onClick={async () => {
              await handleSubmission();
            }}
          />

          <Alert
            openAlert={openAlert}
            setOpenAlert={setOpenAlert}
            alertDetails={alertDetails}
          />
        </div>
      )}
    </>
  );
};
